import { emptyFragment } from '@execonline-inc/execonline-ui';
import { assertNever } from '@kofno/piper';
import { observer } from 'mobx-react';
import { T } from '../../../../Translations';
import ActionButtons from './ActionButtons';
import { InviteTarget, inviteTarget } from './InviteTarget';
import { shareStore } from './Stores/ShareStore';

interface Props {
  onConfirm: () => void;
}

const title = (target: InviteTarget) => {
  switch (target.kind) {
    case 'collection':
      return <T kind="Invite Participants to Collection by Email" />;
    case 'course':
      return <T kind="Invite Participants to Course by Email" />;
    default:
      return assertNever(target);
  }
};

const subtitle = (target: InviteTarget) => {
  switch (target.kind) {
    case 'collection':
      return (
        <T
          kind="Configure invitations for the learning collection: <collectionName/>"
          collectionName={<strong className="text-foreground">{target.collectionName}</strong>}
        />
      );
    case 'course':
      return target.courseName
        .map((courseName) => (
          <T
            kind="Configure invitations for the course: <courseName/> in <collectionName/>"
            courseName={<strong className="text-foreground">{courseName}</strong>}
            collectionName={<strong className="text-foreground">{target.collectionName}</strong>}
          />
        ))
        .getOrElse(() => (
          <T
            kind="Configure invitations for a course in <collectionName/>"
            collectionName={<strong className="text-foreground">{target.collectionName}</strong>}
          />
        ));
    default:
      return assertNever(target);
  }
};

// Header strip shared with the Add Participant and Add Group pages: title and
// one-line description on the left, Cancel and the primary action on the right.
// The title and description say whether this is a collection or a course
// invite (DVLP-7749).
function Header({ onConfirm }: Props) {
  const target = inviteTarget(shareStore.state);
  return (
    <div
      className="border-b border-border bg-background py-6"
      data-intercom-target="share-invite-header"
    >
      <div className="max-w-5xl mx-auto px-12">
        <div className="flex items-start justify-between">
          <div>
            <h1
              className="text-3xl"
              style={{ fontWeight: 'var(--font-weight-semibold)' }}
              data-intercom-target="share-invite-title"
            >
              {target.map(title).getOrElse(emptyFragment)}
            </h1>
            <p className="text-muted-foreground">{target.map(subtitle).getOrElse(emptyFragment)}</p>
          </div>
          <div className="flex items-center gap-3">
            <ActionButtons onConfirm={onConfirm} placement="top" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default observer(Header);
